import { FormLabel } from "@mui/material"

import { imageResizeOptions } from "react-ui-components/components/raffle/raffleSchemaConfig"
import Editor from "react-ui-components/utils/customQuillModules"
import { array, boolean, number, object, string } from "react-ui-components/utils/schema"

export const displayContentConfigSchema: any = {
  type: "object",
  properties: {
    addedPointsAnimation: string("Added Points Animation"),
    dateTimeFormat: string("Raffle DateTime Format", { enum: ["DATETIME_FULL"] }),
    defaultProfileUrl: string("Default Profile Url"),
    imageResizeMode: string("Image Resize Mode", {
      enum: imageResizeOptions,
    }),
    prepayTicketCodePrefixDefault: string("Prepay Ticket Code Prefix Default"),
    pointAnimationLink: string("Points Animation Link"),
    publicationsCarousel: object("Publications Carousel Timer", {
      autoPlay: boolean("Autoplay"),
      intervalSeconds: number("Interval Seconds"),
    }),
    qrCodeIcon: string("QR Code Icon"),
    raffleTypes: array("Raffle Types", { items: { type: "string" } }),
    rafflesImageHeight: number("Raffles Image Height"),
    rafflesScreen: object("Raffles Screen", {
      descriptionLength: number("Description Length"),
      hideAllActions: boolean("Hide All Actions"),
      imageResizeMode: string("Image Resize Mode", {
        enum: imageResizeOptions,
      }),
      rafflesImageHeight: number("Raffles Image Height"),
    }),
    rafflesSectionImageHeight: number("Raffles Section Image Height"),
    sponsorsTitle: string("Sponsors Title"),
    sponsorsTitleKey: string("Sponsors Title Key"),
    termIcon: string("Term Icon"),
    vendorPercentage: number("vendorPercentage"),
    videoPlayback: object("Video Playback", {
      awayBehavior: string("Away Behavior", {
        enum: ["floating", "stopOnAway"],
      }),
      countImpressions: boolean("Count Impressions"),
      countRepeatPlays: boolean("Count Repeat Plays"),
      floatingPopupEnabled: boolean("Floating Popup Enabled"),
      heartbeatSeconds: number("Heartbeat Seconds"),
      raffleVideo: object("Raffle Video", {
        autoplay: boolean("Autoplay"),
        controls: boolean("Controls"),
        fullscreenEnabled: boolean("Fullscreen Enabled"),
        frameBackgroundEnabled: boolean("Frame Background Enabled"),
        frameBorderEnabled: boolean("Frame Border Enabled"),
        frameBorderRadius: number("Frame Border Radius"),
        loop: boolean("Loop"),
        muted: boolean("Muted"),
        objectFit: string("Object Fit", {
          enum: ["contain", "cover", "fill", "none", "scale-down"],
        }),
        showWinViewCount: boolean("Show WinView Count"),
        winViewCountPlacement: string("WinView Count Placement", {
          enum: ["outsideBelow", "insideBelow"],
        }),
      }),
      viewThresholdSeconds: number("View Threshold Seconds"),
    }),
    copyrightMessage: string("Copyright Message"),
    displayDownload: boolean("Display Download"),
  },
}

export const displayContentConfigUiSchema = {
  prepayTicketCodePrefixDefault: {
    "ui:help":
      "Default prefix used when generating prepay ticket QR codes. The raw compact token is appended to this value.",
  },
  copyrightMessage: {
    "ui:widget": ({ onChange, label, ...rest }: any) => (
      <div>
        <FormLabel>{label}</FormLabel>
        <Editor {...rest} onChange={(value: string) => onChange(value)} />
      </div>
    ),
  },
  publicationsCarousel: {
    autoPlay: {
      "ui:help":
        "Auto-rotate ads, featured businesses, and sponsor offer carousels. Default: enabled.",
    },
    intervalSeconds: {
      "ui:help":
        "Seconds each slide is shown before rotating to the next one, for carousels that don't define their own interval (featured businesses, sponsor offers). Default: 6.",
    },
  },
  videoPlayback: {
    awayBehavior: {
      "ui:help":
        "When Floating Popup Enabled is on, floating keeps the active WinView video in a mini-player when it leaves the screen; stopOnAway stops playback.",
    },
    countImpressions: {
      "ui:help":
        "Count a WinView 'representación' (the video being shown/mounted) as its own aggregate, separate from plays. Default: enabled.",
    },
    countRepeatPlays: {
      "ui:help":
        "Count every play, even by a user who already played the video before. When disabled, only a user's first-ever play of a video counts. Default: enabled.",
    },
    floatingPopupEnabled: {
      "ui:help": "Show active raffle videos in a mini-player when they leave the screen. Default: disabled.",
    },
    heartbeatSeconds: {
      "ui:help": "How often active playback sends telemetry while playing. Default: 10.",
    },
    viewThresholdSeconds: {
      "ui:help": "Minimum watched seconds before a WinView count is eligible. Default: 5.",
    },
    raffleVideo: {
      autoplay: {
        "ui:help": "Default autoplay for direct raffle video files. Default: disabled.",
      },
      controls: {
        "ui:help": "Default native controls for direct raffle video files. Default: enabled.",
      },
      fullscreenEnabled: {
        "ui:help": "Allow raffle video fullscreen controls and iframe fullscreen permissions. Default: enabled.",
      },
      frameBackgroundEnabled: {
        "ui:help": "Show a video frame/card background around raffle videos. Default: disabled.",
      },
      frameBorderEnabled: {
        "ui:help": "Show a border around raffle videos. Default: disabled.",
      },
      frameBorderRadius: {
        "ui:help": "Default raffle video frame radius in theme spacing units. Default: 0.",
      },
      loop: {
        "ui:help": "Default loop behavior for direct raffle video files. Default: disabled.",
      },
      muted: {
        "ui:help": "Default muted behavior for direct raffle video files. Default: disabled.",
      },
      objectFit: {
        "ui:help": "How direct raffle videos fit their frame. Default: contain.",
      },
      showWinViewCount: {
        "ui:help": "Show the WinView count for raffle videos. Default: enabled.",
      },
      winViewCountPlacement: {
        "ui:help": "Default placement for the WinView count. Default: outsideBelow.",
      },
    },
  },
}
